import React, { createContext, useContext, useState, useEffect, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { Toaster, toast } from 'sonner';

// Pages
import Landing from './pages/Landing';
import CasaDetail from './pages/CasaDetail';
// El portal privado se carga en demanda: el sitio publico no descarga su codigo.
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Calendar = lazy(() => import('./pages/Calendar'));
const Payments = lazy(() => import('./pages/Payments'));
const FinanceHub = lazy(() => import('./pages/FinanceHub'));
const Fines = lazy(() => import('./pages/Fines'));
const Services = lazy(() => import('./pages/Services'));
const Regulations = lazy(() => import('./pages/Regulations'));
const Messages = lazy(() => import('./pages/Messages'));
const Concierge = lazy(() => import('./pages/Concierge'));
const Profile = lazy(() => import('./pages/Profile'));
const AdminPanel = lazy(() => import('./pages/AdminPanel'));
const MyProperties = lazy(() => import('./pages/MyProperties'));
const PropertyDetail = lazy(() => import('./pages/PropertyDetail'));
const Opportunities = lazy(() => import('./pages/Opportunities'));
const AuthCallback = lazy(() => import('./pages/AuthCallback'));
import { useHubSpotPageView } from './hooks/useHubSpotPageView';

// Use relative URL to avoid Mixed Content issues in the preview environment
export const API = '/api';

const getToken = () => localStorage.getItem('auth_token');

// La sesion puede vivir en el JWT de localStorage o en la cookie httpOnly
// (acceso con Google). Si el backend responde 401 avisamos una sola vez para
// cerrar sesion con un mensaje claro en vez de mostrar "Not authenticated".
const avisarSesionExpirada = () => {
  localStorage.removeItem('auth_token');
  window.dispatchEvent(new CustomEvent('soha:sesion-expirada'));
};

const request = async (method, path, body) => {
  const headers = { 'Content-Type': 'application/json' };
  const token = getToken();
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const res = await fetch(`${API}${path}`, {
    method,
    headers,
    credentials: 'include',
    body: body ? JSON.stringify(body) : undefined,
  });

  if (!res.ok) {
    const err = new Error(`HTTP ${res.status}`);
    err.status = res.status;
    try { err.detail = (await res.json()).detail; } catch {}
    if (res.status === 401 && path !== '/auth/me') {
      err.detail = 'Tu sesion expiro. Vuelve a iniciar sesion para guardar cambios.';
      avisarSesionExpirada();
    }
    throw err;
  }

  const text = await res.text();
  return text ? JSON.parse(text) : null;
};

export const api = {
  get: (path) => request('GET', path),
  post: (path, body) => request('POST', path, body),
  put: (path, body) => request('PUT', path, body),
  delete: (path) => request('DELETE', path),
  upload: async (path, formData) => {
    const headers = {};
    const token = getToken();
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const res = await fetch(`${API}${path}`, { method: 'POST', headers, credentials: 'include', body: formData });
    if (!res.ok) {
      const err = new Error(`HTTP ${res.status}`);
      err.status = res.status;
      try { err.detail = (await res.json()).detail; } catch {}
      if (res.status === 401) {
        err.detail = 'Tu sesion expiro. Vuelve a iniciar sesion para subir imagenes.';
        avisarSesionExpirada();
      }
      throw err;
    }
    return res.json();
  },
};

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = async () => {
    try {
      const data = await api.get('/auth/me');
      setUser(data);
    } catch (error) {
      // Solo un 401 significa sesion invalida; un fallo de red no debe borrar el token.
      if (error.status === 401) {
        localStorage.removeItem('auth_token');
        setUser(null);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkAuth();
  }, []);

  // Sesion expirada (cookie o JWT): cerramos con un aviso claro en vez de
  // dejar la interfaz "logueada" y que falle cada guardado.
  useEffect(() => {
    const alExpirar = () => {
      setUser(null);
      toast.error('Tu sesion expiro. Vuelve a iniciar sesion.');
      if (!['/', '/login'].includes(window.location.pathname)) {
        window.location.href = '/login';
      }
    };
    window.addEventListener('soha:sesion-expirada', alExpirar);
    return () => window.removeEventListener('soha:sesion-expirada', alExpirar);
  }, []);

  // Al volver a la pestaña revalidamos contra el servidor (fuente de verdad).
  useEffect(() => {
    const alEnfocar = () => {
      if (document.visibilityState === 'visible' && user) checkAuth();
    };
    document.addEventListener('visibilitychange', alEnfocar);
    return () => document.removeEventListener('visibilitychange', alEnfocar);
  }, [user]);

  const login = async (email, password) => {
    const data = await api.post('/auth/login', { email, password });
    if (data.access_token) {
      localStorage.setItem('auth_token', data.access_token);
    }
    setUser(data.user);
    return data;
  };

  const register = async (data) => {
    const result = await api.post('/auth/register', data);
    if (result.access_token) {
      localStorage.setItem('auth_token', result.access_token);
    }
    setUser(result.user);
    return result;
  };

  const logout = async () => {
    try {
      await api.post('/auth/logout', {});
    } catch (error) {
      console.error('Logout error:', error);
    }
    localStorage.removeItem('auth_token');
    setUser(null);
  };

  const processGoogleAuth = async (sessionId) => {
    const data = await api.post('/auth/session', { session_id: sessionId });
    // Guardamos tambien el token: si la cookie httpOnly se pierde o expira,
    // el header Authorization mantiene la sesion viva igual que en el login clasico.
    if (data.access_token) {
      localStorage.setItem('auth_token', data.access_token);
    }
    setUser(data.user || data);
    return data;
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      loading, 
      login, 
      register, 
      logout, 
      processGoogleAuth,
      checkAuth,
      setUser
    }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <img 
            src={`${process.env.REACT_APP_BACKEND_URL}/api/files/brand/soha_logo.png`}
            alt="SOHA"
            className="h-10 w-auto object-contain mx-auto mb-4 animate-pulse"
            style={{ filter: 'brightness(0)' }}
          />
          <p className="text-muted-foreground text-sm">Cargando...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  return children;
};

// Admin Route Component
const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <img 
            src={`${process.env.REACT_APP_BACKEND_URL}/api/files/brand/soha_logo.png`}
            alt="SOHA"
            className="h-10 w-auto object-contain mx-auto mb-4 animate-pulse"
            style={{ filter: 'brightness(0)' }}
          />
          <p className="text-muted-foreground text-sm">Cargando...</p>
        </div>
      </div>
    );
  }

  if (!user || user.role !== 'admin') {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

const Cargando = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <img src="/brand/soha-principal-verde.png" alt="SOHA Reserve" width="800" height="318"
      className="h-10 w-auto object-contain animate-pulse" />
  </div>
);

// App Router with session_id detection
function AppRouter() {
  const location = useLocation();
  useHubSpotPageView();
  
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  // Check URL fragment for session_id synchronously during render
  if (location.hash?.includes('session_id=')) {
    return <Suspense fallback={<Cargando />}><AuthCallback /></Suspense>;
  }

  return (
    <Suspense fallback={<Cargando />}>
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Landing showLogin />} />
      <Route path="/casa/:slug" element={<CasaDetail />} />
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <Dashboard />
        </ProtectedRoute>
      } />
      <Route path="/calendar" element={
        <ProtectedRoute>
          <Calendar />
        </ProtectedRoute>
      } />
      <Route path="/payments" element={
        <ProtectedRoute>
          <Payments />
        </ProtectedRoute>
      } />
      <Route path="/finance" element={
        <ProtectedRoute>
          <FinanceHub />
        </ProtectedRoute>
      } />
      <Route path="/fines" element={
        <ProtectedRoute>
          <Fines />
        </ProtectedRoute>
      } />
      <Route path="/services" element={
        <ProtectedRoute>
          <Services />
        </ProtectedRoute>
      } />
      <Route path="/regulations" element={
        <ProtectedRoute>
          <Regulations />
        </ProtectedRoute>
      } />
      <Route path="/messages" element={
        <ProtectedRoute>
          <Messages />
        </ProtectedRoute>
      } />
      <Route path="/concierge" element={
        <ProtectedRoute>
          <Concierge />
        </ProtectedRoute>
      } />
      <Route path="/my-properties" element={
        <ProtectedRoute>
          <MyProperties />
        </ProtectedRoute>
      } />
      <Route path="/properties/:propertyId" element={
        <ProtectedRoute>
          <PropertyDetail />
        </ProtectedRoute>
      } />
      <Route path="/opportunities" element={
        <ProtectedRoute>
          <Opportunities />
        </ProtectedRoute>
      } />
      <Route path="/profile" element={
        <ProtectedRoute>
          <Profile />
        </ProtectedRoute>
      } />
      <Route path="/admin" element={
        <AdminRoute>
          <AdminPanel />
        </AdminRoute>
      } />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster 
          position="top-right" 
          richColors 
          toastOptions={{
            style: {
              fontFamily: 'Manrope, sans-serif',
            }
          }}
        />
        <AppRouter />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
